'use client'

import { ChevronDown } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/lib/ui-kit/components/ui/Dialog.component'
import { cn } from '@/lib/utils'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/lib/ui-kit/components/ui/Accordion.component'
import VerifiedBadge from './VerifiedBadge.component'

interface WineLotsModalProps {
  isOpen: boolean
  onClose: () => void
  wineryData: any
  wineryVerification: any[]
}

export function WineLotsModal({
  isOpen,
  onClose,
  wineryData,
  wineryVerification,
}: WineLotsModalProps) {
  const lots: any = Array.isArray(wineryData) ? wineryData : wineryData.products

  if (!lots?.length) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-h-[90dvh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {!wineryData.certificate_number
              ? `Wine Lots: ${lots.length}`
              : 'Certificate Information'}
          </DialogTitle>
        </DialogHeader>
        {wineryData.certificate_number && (
          <>
            <div className="space-y-2">
              <div className="flex flex-col">
                <div className="grid-cols-2 gap-5 border-b border-dashed border-gray-200 py-3 xl:grid">
                  <div className="paragraph-s font-medium">Certificate No.</div>
                  <div className="paragraph-s flex font-normal xl:justify-end">
                    {wineryData.certificate_number || '-'}
                  </div>
                </div>
                <div className="grid-cols-2 gap-5 border-b border-dashed border-gray-200 py-3 xl:grid">
                  <div className="paragraph-s font-medium">
                    Certificate Type
                  </div>
                  <div className="paragraph-s flex font-normal xl:justify-end">
                    Conformity
                  </div>
                </div>
                <div className="grid-cols-2 gap-5 border-b border-dashed border-gray-200 py-3 xl:grid">
                  <div className="paragraph-s font-medium">Export Country</div>
                  <div className="paragraph-s flex font-normal xl:justify-end">
                    {wineryData.export_country || '-'}
                  </div>
                </div>
                <div className="grid-cols-2 gap-5 border-b border-dashed border-gray-200 py-3 xl:grid">
                  <div className="paragraph-s font-medium">Company Name</div>
                  <div className="paragraph-s flex font-normal capitalize xl:justify-end">
                    {wineryData.company_name || '-'}
                  </div>
                </div>
                <div className="grid-cols-2 gap-5 border-b border-dashed border-gray-200 py-3 xl:grid">
                  <div className="paragraph-s font-medium">Company RS Code</div>
                  <div className="paragraph-s flex font-normal xl:justify-end">
                    {wineryData.company_rs_code || '-'}
                  </div>
                </div>
                <div className="grid-cols-2 gap-5 border-b border-dashed border-gray-200 py-3 xl:grid">
                  <div className="paragraph-s font-medium">
                    Exam Protocol Number
                  </div>
                  <div className="paragraph-s flex text-right font-normal xl:justify-end">
                    {wineryData.exam_protocol_number || '-'}
                  </div>
                </div>
                <div className="grid-cols-2 gap-5 border-b border-dashed border-gray-200 py-3 xl:grid">
                  <div className="paragraph-s font-medium">
                    Tasting Protocol Number
                  </div>
                  <div className="paragraph-s flex text-right font-normal xl:justify-end">
                    {wineryData.tasting_protocol_number || '-'}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 text-2xl font-bold">
              <strong>Wine Lots:</strong> {lots.length}
            </div>
          </>
        )}
        <div className={`${wineryData.certificate_number ? 'mt-2' : 'mt-4'}`}>
          <Accordion type="single" collapsible>
            {lots.map((lot: any, index: number) => (
              <AccordionItem
                key={index}
                value={`lot-${index}`}
                className="border-gray-400 first:border-t"
                hasBottomBorder
              >
                <AccordionTrigger
                  className="flex items-center justify-between p-2 xl:p-4 [&[data-state=open]>svg]:rotate-180"
                  disableDefaultTrigger
                >
                  <div className="flex w-full items-center justify-between gap-4">
                    <span className="mr-2 text-lg font-medium">
                      Lot Number: {lot.lot_number}
                    </span>
                    <div className="mr-2">
                      {wineryVerification[index] && <VerifiedBadge />}
                    </div>
                  </div>
                  <ChevronDown
                    className={cn(
                      'h-5 w-5 shrink-0 transition-transform duration-200',
                    )}
                  />
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  {lot.number_of_bottles && (
                    <div className="space-y-2">
                      <div className="flex flex-col ">
                        <div className="grid-cols-2 gap-5 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Bottle Count
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.number_of_bottles || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Bottling Date
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.bottling_date || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Wine Name
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.wine_name || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Wine Color
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.wine_color || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Wine Type
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.wine_type || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Vintage Year
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.vintage_year || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Aging Recipient
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.aging_recipient || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Aging Time
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.aging_time || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Storage Vessel
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.storage_vessel || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Harvest Date
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.harvest_date || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Harvest Location
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.harvest_location || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Pressing Date
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.pressing_date || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Aging Time
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.aging_time || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Fermentation Vessel
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.fermentation_vessel || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Fermentation Duration
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.fermentation_duration || '-'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {lot.bottle_count_in_lot && (
                    <div className="space-y-2">
                      <div className="flex flex-col">
                        <div className="grid-cols-2 gap-5 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Bottle Count
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.bottle_count_in_lot || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-5 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Bottle Type
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.bottle_type || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-5 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Bottle Volume
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.bottle_volume || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Bottling Date
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.bottling_date || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Wine Name
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.wine_name || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Wine Color
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.color || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Wine Type
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.type || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Delayed on Chacha
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.delayed_on_chacha ? 'Yes' : 'No'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Grape Variety
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.grape_variety || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Sugar Content Category
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.sugar_content_category || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Harvest Year
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.harvest_year || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 border-b border-dashed border-gray-200 py-3 xl:grid">
                          <div className="paragraph-s font-medium">Origin</div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.origin || '-'}
                          </div>
                        </div>
                        <div className="grid-cols-2 gap-2 py-3 xl:grid">
                          <div className="paragraph-s font-medium">
                            Viticulture Area
                          </div>
                          <div className="paragraph-s flex font-normal xl:justify-end">
                            {lot.viticulture_area || '-'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </DialogContent>
    </Dialog>
  )
}
